import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Typography, Flex, Avatar, Button, ConfigProvider } from "antd";
import { CloseOutlined, InstagramOutlined } from "@ant-design/icons";
import type { IStylistCardProps } from ".";
import { useBookingApi, REAL_BOOKING_API } from "@/app/components/NewAppointmentModal/bookingApi";
import { UserData, UserExperienceResponse } from "@/lib/api/types";
import OpeningLoading from "@/app/components/OpeningLoading";
import { MemberTagBadges } from "@/app/components/MemberTags";
import PhotoGrid from "@/app/photos/components/PhotoGrid";
import LazyVideo from "@/app/components/LazyVideo";
import {
  filterStylistPhotos,
  stylistPhotoKey,
  STYLIST_SERVICES,
  type StylistTag,
} from "@/lib/stylists";

import styles from "./miniProfile.module.scss";

// SCROLL STORYBOARD: video fills the sheet behind the identity → its bottom
// approaches the pinned header → the header surface fades to solid ink.
const HEADER_FADE_DISTANCE = 80;

const getPhotoType = (type: string): "image" | "video" =>
  type === "video" ? "video" : "image";

const MiniProfile = ({
  userId,
  name,
  username,
  video,
  photo,
  profilePhotos,
  bio,
  schedule,
  locationNames = [],
  instagram,
  tags = [],
  showTags = false,
  photoCategories = {},
  onClose,
}: Pick<
  IStylistCardProps,
  "userId" | "name" | "username" | "photo" | "video"
> & {
  profilePhotos?: UserData["photos"];
  bio?: string;
  workingSince?: string | null;
  schedule?: IStylistCardProps["schedule"];
  locationNames?: string[];
  instagram?: string;
  /** Openings team member tags; the experience read's profile copy wins. */
  tags?: StylistTag[];
  /**
   * Show the tags as the "Useful info" section. Only the location page's
   * booking card asks (Oct 21): the Team directory's profile shows none,
   * even when the experience read returns them.
   */
  showTags?: boolean;
  photoCategories?: Record<string, string[]>;
  onClose: () => void;
}) => {
  const bookingApi = useBookingApi();
  const [data, setData] = useState<UserExperienceResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [reviewsShown, setReviewsShown] = useState(2);
  const [allPhotosShown, setAllPhotosShown] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  // Open on all work: many stylists leave their photos untagged.
  const [photoService, setPhotoService] = useState("");
  const usefulInfoTitleId = useId();

  const frameRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const bioRef = useRef<HTMLElement>(null);
  const displayVideo = data?.profileVideo ?? video;
  const overlaysVideo = !isLoading && !error && Boolean(displayVideo?.url);

  const updateHeaderOpacity = () => {
    if (!frameRef.current || !headerRef.current || !scrollRef.current || !videoRef.current) return;
    const fadeEnd = Math.max(1, videoRef.current.offsetHeight - headerRef.current.offsetHeight);
    const fadeStart = Math.max(0, fadeEnd - HEADER_FADE_DISTANCE);
    const opacity = Math.min(1, Math.max(0,
      (scrollRef.current.scrollTop - fadeStart) / (fadeEnd - fadeStart),
    ));
    frameRef.current.style.setProperty("--profile-header-opacity", String(opacity));
  };

  // The header floats over the scroll body, which starts below it. Its height
  // is not fixed (the name row and location line can wrap), so the scroll
  // body reads the measured height instead of a fixed inset.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const header = headerRef.current;
    if (!frame || !header) return;
    const syncHeaderHeight = () =>
      frame.style.setProperty("--profile-header-height", `${header.offsetHeight}px`);
    syncHeaderHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!overlaysVideo) return;
    updateHeaderOpacity();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeaderOpacity);
    if (headerRef.current) observer.observe(headerRef.current);
    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [overlaysVideo]);

  const locationId = schedule?.locationId;
  useEffect(() => {
    let active = true;
    const options = {
      profileView: true,
      photoSource: "portfolio" as const,
      ...(locationId ? { locationId } : {}),
    };
    (async () => {
      try {
        const result = await bookingApi.getStylistExperience(userId, options);
        if (active) setData(result);
      } catch (error) {
        try {
          const profile = await bookingApi.getUser(username);
          try {
            const result = await bookingApi.getStylistExperience(profile._id, options);
            if (active) setData({
              ...result,
              // A profile fallback may resolve another business membership.
              workingSince: profile._id === userId ? result.workingSince : undefined,
            });
          } catch {
            if (active) setData({
              photos: profile.photos.map((profilePhoto) => ({
                ...profilePhoto,
                _id: profilePhoto._id,
                src: profilePhoto.src,
                alt: profilePhoto.alt,
                type: getPhotoType(profilePhoto.type),
                service: profilePhoto.service,
              })),
              stats: {
                totalHaircuts: 0,
                bio: profile.bio,
              },
              reviews: [],
            });
          }
        } catch (fallbackError) {
          if (active) setError(fallbackError as Error);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [bookingApi, userId, username, locationId]);

  const instagramHandle = data?.instagramHandle ?? instagram;
  const displayLocations = data?.profile?.locationNames ?? locationNames;
  const displayTags = showTags ? (data?.profile?.tags ?? tags) : [];
  const displayBio = data?.stats.bio?.trim() || bio?.trim();
  const profileMemberId = data?.profile?.memberId ?? userId;
  const totalHaircuts = data?.stats.totalHaircuts ?? 0;
  // Rounded down to the hundred: an experience signal, not an exact tally.
  const haircutCount =
    totalHaircuts > 100
      ? `Served ${(Math.floor(totalHaircuts / 100) * 100).toLocaleString("en-US")}+ haircuts`
      : undefined;
  const profileHeader = (
    <Flex
      justify="space-between"
      align="flex-start"
      ref={headerRef}
      // Flex resets padding to zero; keep this inset independent of CSS load order.
      // A short bottom: the scroll body adds its own 12px above the first section.
      style={{ padding: "12px 12px 6px" }}
      className={`${styles.header} ${overlaysVideo ? styles.headerOverVideo : ""}`}
      data-testid="mini-profile-header"
    >
      <div className={styles.identity}>
        <div className={styles.nameRow}>
          <Avatar size={24} src={photo || undefined} />
          <Typography.Text className={styles.name} strong>{name}</Typography.Text>
          {instagramHandle && (
            <a
              className={styles.instagram}
              href={`https://www.instagram.com/${instagramHandle}/`}
              target="_blank"
              rel="noopener noreferrer"
              title={`@${instagramHandle}`}
              aria-label={`${name} on Instagram (opens in a new tab)`}
            >
              <InstagramOutlined aria-hidden="true" />
            </a>
          )}
        </div>
        <p className={styles.metadata} aria-busy={isLoading}>
          {displayLocations.length > 0 && (
            <span>{displayLocations.join(" · ")}</span>
          )}
          {displayLocations.length > 0 && haircutCount && " · "}
          {haircutCount && <span>{haircutCount}</span>}
          {isLoading && !displayLocations.length && <span className={styles.metadataPlaceholder} aria-hidden="true" />}
        </p>
      </div>
      <Button
        className={styles.close}
        color="default"
        variant="text"
        onClick={onClose}
        icon={<CloseOutlined />}
        aria-label={`Close ${name} profile`}
      />
    </Flex>
  );

  const sourcePhotos = data?.photoSource === "portfolio"
    ? data.photos
    : profilePhotos ?? data?.photos ?? [];
  const allPhotos = sourcePhotos.map((photo) => ({
    ...photo,
    serviceIds:
      photo.serviceIds ?? photoCategories[stylistPhotoKey(photo.src)] ??
      STYLIST_SERVICES.filter((service) =>
        service.categories.includes(photo.service?.category ?? 0),
      ).map((service) => service.id),
  }));
  const photos = filterStylistPhotos(allPhotos, photoService);
  const photoServices = STYLIST_SERVICES.filter(
    (service) =>
      service.id === photoService ||
      allPhotos.some((photo) => photo.serviceIds.includes(service.id)),
  );
  const visiblePhotos = allPhotosShown ? photos : photos.slice(0, 6);

  return (
    <div ref={frameRef} className={styles.profileFrame}>
      {profileHeader}
      <div
        ref={scrollRef}
        className={`${styles.scrollBody} ${overlaysVideo ? styles.scrollOverVideo : ""}`}
        onScroll={overlaysVideo ? updateHeaderOpacity : undefined}
        role={isLoading ? "group" : "region"}
        aria-label={`${name} profile content`}
        tabIndex={0}
      >
        {isLoading ? (
          <div className={styles.loadingBody} role="status" aria-label={`Loading ${name} profile`}>
            <div className={styles.loadingMedia} aria-hidden="true" />
            <OpeningLoading />
          </div>
        ) : error ? (
          <div className={styles.errorBody} role="alert">Failed to load profile data. Please close and try again.</div>
        ) : (
          <>
            {overlaysVideo && displayVideo && (
              <div ref={videoRef} className={styles.videoBanner} data-testid="mini-profile-video-banner">
                <LazyVideo
                  key={displayVideo.url}
                  url={displayVideo.url}
                  poster={displayVideo.thumbnail}
                  width="100%"
                  height="100%"
                />
              </div>
            )}
            <Flex
              vertical
              style={{
                width: "100%",
                padding: "12px",
              }}
              gap={24}
            >
              {displayBio && (
                <section ref={bioRef} aria-label={`About ${name}`} className={styles.bio}>
                  <ConfigProvider locale={{ locale: "en", Text: { expand: "Read more", collapse: "Read less" } }}>
                    <Typography.Paragraph
                      style={{ margin: 0, whiteSpace: "pre-line" }}
                      ellipsis={{
                        rows: 2,
                        expandable: "collapsible",
                        expanded: bioExpanded,
                        onExpand: (_event, { expanded }) => {
                          // Keep the start of a long bio visible when collapsing it.
                          const scroller = scrollRef.current;
                          const section = bioRef.current;
                          const header = headerRef.current;
                          if (!expanded && scroller && section && header) {
                            const hiddenByHeader = header.getBoundingClientRect().bottom - section.getBoundingClientRect().top;
                            if (hiddenByHeader > 0) scroller.scrollTop -= hiddenByHeader;
                          }
                          setBioExpanded(expanded);
                        },
                      }}
                    >
                      {displayBio}
                    </Typography.Paragraph>
                  </ConfigProvider>
                </section>
              )}
              {(allPhotos.length > 0 || photoService) && (
                <section className={styles.gallery} aria-label={`Work by ${name}`}>
                  {photoServices.length > 0 && (
                    <div
                      className={styles.photoFilters}
                      role="group"
                      aria-label="Filter work photos"
                    >
                      {[{ id: "", label: "All work" }, ...photoServices].map(
                        (service) => (
                          <button
                            key={service.id}
                            type="button"
                            aria-pressed={photoService === service.id}
                            onClick={() => {
                              setPhotoService(service.id);
                              setAllPhotosShown(false);
                            }}
                          >
                            {service.label}
                          </button>
                        ),
                      )}
                    </div>
                  )}
                  {photos.length === 0 && (
                    <p className={styles.photoEmpty}>
                      No matching photos to show. Select All work to explore this
                      stylist’s portfolio.
                    </p>
                  )}
                  <PhotoGrid
                    trackingEnabled={bookingApi === REAL_BOOKING_API}
                    photos={visiblePhotos.map((item) => ({
                      ...item,
                      type: getPhotoType(item.type),
                      user: { _id: profileMemberId, name, photo, username },
                    }))}
                    grid="AUTO"
                  />
                  {!allPhotosShown && photos.length > 6 && (
                    <Button
                      type="link"
                      className={styles.more}
                      onClick={() => setAllPhotosShown(true)}
                    >
                      See all photos ({photos.length})
                    </Button>
                  )}
                </section>
              )}
              {/* The tag pills under their own heading (Oct 21): bare pills in
                  the header looked out of place. */}
              {displayTags.length > 0 && (
                <section aria-labelledby={usefulInfoTitleId}>
                  <Typography.Text strong id={usefulInfoTitleId}>
                    Useful info
                  </Typography.Text>
                  <MemberTagBadges
                    tags={displayTags}
                    label={`About ${name}`}
                    className={styles.usefulInfoTags}
                  />
                </section>
              )}
              {data && data?.reviews?.length > 0 && (
                <div>
                  <Typography.Text strong>Customer reviews</Typography.Text>
                  <Flex vertical gap={4} style={{ marginTop: 4 }}>
                    {data.reviews
                      .slice(0, reviewsShown)
                      .map((review, i: number) => (
                        <div
                          key={`review-${i}`}
                          style={{
                            background: "#f5f5f5",
                            borderRadius: 10,
                            borderTopLeftRadius: 3,
                            padding: "5px 8px",
                          }}
                        >
                          {review.customerName && (
                            <Typography.Text strong style={{ fontSize: 11 }}>
                              {review.customerName}
                            </Typography.Text>
                          )}
                          <Typography.Paragraph
                            style={{
                              margin: "1px 0 2px",
                              fontSize: 12,
                              lineHeight: 1.35,
                            }}
                          >
                            {review.note}
                          </Typography.Paragraph>
                          {review.createdAt && (
                            <Flex justify="flex-end">
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 10 }}
                              >
                                {new Date(review.createdAt).toLocaleDateString(
                                  "en-US",
                                  { month: "short", year: "numeric" },
                                )}
                              </Typography.Text>
                            </Flex>
                          )}
                        </div>
                      ))}
                  </Flex>
                  {data.reviews.length > reviewsShown && (
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingLeft: 0, marginTop: 6 }}
                      onClick={() =>
                        setReviewsShown((prev) =>
                          Math.min(prev + 5, data.reviews.length),
                        )
                      }
                    >
                      More reviews
                    </Button>
                  )}
                </div>
              )}
            </Flex>
          </>
        )}
      </div>
    </div>
  );
};

export default MiniProfile;
